import {
	ChevronLeftIcon,
	ChevronRightIcon,
	PlusIcon,
} from "@heroicons/react/24/outline";
import { format, isSameMonth } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import type { TwoWeekWindow } from "../../../hooks/track/useTwoWeekWindow";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { Event } from "../../../types/event";
import { EventLegend } from "../EventLegend";
import { TwoWeekCalendar } from "../TwoWeekCalendar";
import styles from "./TrackOverview.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

interface TrackOverviewProps {
	events: Event[] | undefined;
	window: TwoWeekWindow;
	onDayTap: (dayIso: string, eventsForDay: Event[]) => void;
	/** Phase 37 wiring (D-16) — TrackPage owns AddEventSheet open state. */
	onAddEvent?: () => void;
	/** Phase 38 D-12 — propagated from TrackPage's deep-link consumer. */
	focusDayIso?: string | null;
}

export function TrackOverview({
	events,
	window,
	onDayTap,
	onAddEvent,
	focusDayIso,
}: TrackOverviewProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;

	// CAL-02: month label(s) — joined with "/" when window spans two months
	const firstMonth = format(window.windowStart, "MMMM", {
		locale: dateFnsLocale,
	});
	const lastMonth = format(window.windowEnd, "MMMM", {
		locale: dateFnsLocale,
	});
	const monthLabel = isSameMonth(window.windowStart, window.windowEnd)
		? firstMonth
		: t("calendar.monthLabelJoin", { first: firstMonth, second: lastMonth });

	return (
		<section className={styles.section}>
			<div className={styles.header}>
				<h2 className={styles.title}>{t("overview.title")}</h2>
				<button
					type="button"
					className={styles.addBtn}
					aria-label={t("addEvent.ariaLabel")}
					onClick={onAddEvent}
				>
					<PlusIcon className={styles.plusIcon} />
				</button>
			</div>

			<div className={styles.monthRow}>
				<span className={styles.monthLabel}>{monthLabel}</span>
				<div className={styles.monthNav}>
					<button
						type="button"
						className={styles.navBtn}
						aria-label={t("calendar.prevTwoWeeks")}
						onClick={window.prev}
					>
						{/* Pitfall 1: ChevronLeftIcon is SEMANTICALLY "previous" — logical CSS handles RTL flip */}
						<ChevronLeftIcon className={styles.navIcon} />
					</button>
					<button
						type="button"
						className={styles.navBtn}
						aria-label={t("calendar.nextTwoWeeks")}
						onClick={window.next}
					>
						<ChevronRightIcon className={styles.navIcon} />
					</button>
				</div>
			</div>

			<EventLegend />

			<TwoWeekCalendar
				windowStart={window.windowStart}
				events={events ?? []}
				onDayTap={onDayTap}
				focusDayIso={focusDayIso}
			/>
		</section>
	);
}
